import { useEffect, useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { ArrowLeft, Check, ChevronDown, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { apiService } from '@/services/api'
import { canCreateCampaign } from '@/utils/permissions'
import { cn } from '@/lib/utils'
import type { GameTemplate, User } from '@/types'

const SUBJECTS = [
  'Matemática',
  'Ciências',
  'História',
  'Literatura',
  'Geografia',
  'Física',
  'Química',
  'Biologia',
  'Ciência da Computação',
  'Língua Portuguesa',
  'Estudos Sociais',
  'Arte e Criatividade',
  'Filosofia',
  'Economia',
  'Psicologia',
]

const OTHER_SUBJECT = '__outra__'

type FieldErrors = { name?: string; subject?: string; template?: string }

/** Pluraliza a contagem do badge dos cards ("1 classe", "4 atributos"). */
function countLabel(n: number, singular: string, plural: string) {
  return `${n} ${n === 1 ? singular : plural}`
}

interface CreateCampaignScreenProps {
  user?: User
}

/**
 * Criação de campanha em um passo (rota /campanhas/nova), mobile-first.
 * Decisão de design em docs/PLANO_CRIACAO_CAMPANHA.md: criar é leve (nome,
 * matéria, sistema); a customização profunda acontece depois, na página da
 * campanha. Todo template — inclusive "Começar do Zero" — nasce jogável.
 */
export function CreateCampaignScreen({ user }: CreateCampaignScreenProps) {
  const navigate = useNavigate()

  const [name, setName] = useState('')
  const [subjectChoice, setSubjectChoice] = useState('')
  const [subjectOther, setSubjectOther] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [description, setDescription] = useState('')
  const [imageUrl, setImageUrl] = useState('')

  const [templates, setTemplates] = useState<GameTemplate[]>([])
  const [loadingTemplates, setLoadingTemplates] = useState(true)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [submitting, setSubmitting] = useState(false)

  const nameRef = useRef<HTMLDivElement>(null)
  const subjectRef = useRef<HTMLDivElement>(null)
  const templateRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    apiService
      .getTemplates()
      .then(setTemplates)
      .catch(() => toast.error('Não foi possível carregar os sistemas de jogo.'))
      .finally(() => setLoadingTemplates(false))
  }, [])

  if (user && !canCreateCampaign(user)) {
    return <Navigate to="/pagina-inicial" replace />
  }

  const subject = subjectChoice === OTHER_SUBJECT ? subjectOther.trim() : subjectChoice

  const clearError = (field: keyof FieldErrors) =>
    setErrors((prev) => ({ ...prev, [field]: undefined }))

  const handleCreate = async () => {
    const nextErrors: FieldErrors = {}
    if (!name.trim()) nextErrors.name = 'Dê um nome para a campanha.'
    if (!subject) nextErrors.subject = 'Escolha a matéria.'
    if (!templateId) nextErrors.template = 'Escolha um sistema de jogo.'

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      const firstInvalid = nextErrors.name ? nameRef : nextErrors.subject ? subjectRef : templateRef
      firstInvalid.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    setSubmitting(true)
    try {
      const table = await apiService.createTable({
        name: name.trim(),
        description: description.trim(),
        subject,
        image_url: imageUrl.trim() || undefined,
        template_id: templateId,
      })
      toast.success('Campanha criada! Passo 2: configure o sistema de jogo.')
      // Tela 2 do wizard: configurar o sistema antes de chegar ao /detalhes
      navigate(`/campanhas/${table.id}/sistema`)
    } catch {
      // Dados permanecem na tela para nova tentativa.
      toast.error('Não foi possível criar a campanha. Tente novamente.')
      setSubmitting(false)
    }
  }

  return (
    <div className="mx-auto w-full max-w-md pb-28">
      {/* Cabeçalho */}
      <div className="flex items-center gap-3 pb-1 pt-2">
        <Button
          variant="outline"
          size="icon"
          aria-label="Voltar para a página inicial"
          onClick={() => navigate('/pagina-inicial')}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-black italic tracking-tighter">Nova campanha</h1>
          <p className="text-xs text-muted-foreground">Três passos e a mesa está no ar</p>
        </div>
      </div>

      {/* Nome */}
      <div ref={nameRef} className="mt-5 space-y-2">
        <Label
          htmlFor="campaign-name"
          className="text-[10px] font-black uppercase tracking-widest text-muted-foreground"
        >
          Nome da campanha
        </Label>
        <Input
          id="campaign-name"
          placeholder="Ex.: A Jornada dos Elétrons"
          value={name}
          maxLength={80}
          aria-invalid={!!errors.name}
          className={cn(errors.name && 'border-destructive')}
          onChange={(e) => {
            setName(e.target.value)
            clearError('name')
          }}
        />
        {errors.name && <p className="text-xs font-semibold text-destructive">{errors.name}</p>}
      </div>

      {/* Matéria */}
      <div ref={subjectRef} className="mt-5 space-y-2">
        <Label
          htmlFor="campaign-subject"
          className="text-[10px] font-black uppercase tracking-widest text-muted-foreground"
        >
          Matéria
        </Label>
        <Select
          value={subjectChoice}
          onValueChange={(v) => {
            setSubjectChoice(v)
            clearError('subject')
          }}
        >
          <SelectTrigger
            id="campaign-subject"
            aria-invalid={!!errors.subject}
            className={cn('w-full', errors.subject && 'border-destructive')}
          >
            <SelectValue placeholder="Selecione a área de estudo" />
          </SelectTrigger>
          <SelectContent>
            {SUBJECTS.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
            <SelectItem value={OTHER_SUBJECT}>Outra…</SelectItem>
          </SelectContent>
        </Select>
        {subjectChoice === OTHER_SUBJECT && (
          <Input
            placeholder="Qual matéria?"
            value={subjectOther}
            maxLength={60}
            aria-label="Qual matéria?"
            onChange={(e) => {
              setSubjectOther(e.target.value)
              clearError('subject')
            }}
          />
        )}
        {errors.subject && <p className="text-xs font-semibold text-destructive">{errors.subject}</p>}
      </div>

      {/* Sistema de jogo */}
      <div ref={templateRef} className="mt-5 space-y-2">
        <span className="block text-[10px] font-black uppercase tracking-widest text-muted-foreground">
          Sistema de jogo
        </span>

        {loadingTemplates ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid gap-2.5" role="radiogroup" aria-label="Sistema de jogo">
            {templates.map((template) => {
              const selected = templateId === template.id
              return (
                <button
                  key={template.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => {
                    setTemplateId(template.id)
                    clearError('template')
                  }}
                  className={cn(
                    'relative w-full rounded-xl border-2 p-3.5 pr-12 text-left transition-colors',
                    selected
                      ? 'border-primary bg-accent'
                      : 'border-border bg-card hover:border-primary/40',
                    errors.template && !selected && 'border-destructive/40'
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full border-2',
                      selected ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card'
                    )}
                  >
                    {selected && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                  </span>
                  <span className="block text-sm font-extrabold">{template.name}</span>
                  <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                    {template.description}
                  </span>
                  <span className="mt-2 inline-block rounded-full bg-accent px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-widest text-accent-foreground">
                    {countLabel(template.classes.length, 'classe', 'classes')} ·{' '}
                    {countLabel(template.attributes.length, 'atributo', 'atributos')} · editável
                  </span>
                </button>
              )
            })}
          </div>
        )}
        {errors.template && <p className="text-xs font-semibold text-destructive">{errors.template}</p>}
      </div>

      {/* Detalhes opcionais */}
      <Collapsible className="mt-6 border-t border-dashed pt-4">
        <CollapsibleTrigger className="group flex items-center gap-1.5 text-sm font-bold text-primary">
          Mais detalhes (opcional)
          <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <p className="mt-2 text-xs text-muted-foreground">
            Descrição e imagem aparecem no card que os alunos veem. Dá para preencher depois, em Editar
            campanha.
          </p>
          <div className="mt-3 space-y-2">
            <Label
              htmlFor="campaign-description"
              className="text-[10px] font-black uppercase tracking-widest text-muted-foreground"
            >
              Descrição
            </Label>
            <Textarea
              id="campaign-description"
              placeholder="Cenário da campanha e objetivos de aprendizado"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="mt-3 space-y-2">
            <Label
              htmlFor="campaign-image"
              className="text-[10px] font-black uppercase tracking-widest text-muted-foreground"
            >
              URL da imagem
            </Label>
            <Input
              id="campaign-image"
              type="url"
              placeholder="https://exemplo.com/imagem.jpg"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Botão fixo no rodapé (mobile-first; sem <form> para Enter não
          submeter no meio do preenchimento) */}
      <div className="fixed inset-x-0 bottom-0 z-10 bg-gradient-to-t from-background via-background/95 to-transparent px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4">
        <div className="mx-auto w-full max-w-md">
          <Button
            type="button"
            size="lg"
            disabled={submitting}
            onClick={handleCreate}
            className="w-full font-black uppercase tracking-widest"
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {submitting ? 'Criando…' : 'Criar campanha'}
          </Button>
        </div>
      </div>
    </div>
  )
}
